/**
 * Shared helpers for the rich-text dynamic-key system.
 *
 * Two placeholder formats coexist for back-compat:
 *   1. Legacy span markup:  <span data-ejs-path="path">{{path}}</span>
 *   2. Current clean token: {{path}}
 *
 * On export we convert both into the unescaped EJS expression `<%= path %>`.
 * On live preview we resolve both against the provided data object.
 * On canvas we style both as inline blue marker pills.
 */

const SPAN_RE = /<span[^>]*data-ejs-path="([^"]+)"[^>]*>[\s\S]*?<\/span>/g;
const TOKEN_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_.[\]]*)\s*\}\}/g;

/**
 * Convert rich-text HTML to EJS-expression-ready HTML.
 * Used by the Export EJS code generator.
 */
export function unwrapToEjs(html) {
  if (!html) return "";
  return html
    .replace(SPAN_RE, (_, path) => `<%= ${path} %>`)
    .replace(TOKEN_RE, (_, path) => `<%= ${path} %>`);
}

/**
 * Resolve rich-text HTML against a sample data object for the live preview.
 * `escapeHtml` and `resolve` are injected so the helper stays generator-agnostic.
 *
 * @param {string} html - rich text HTML containing placeholders.
 * @param {object} data - sample data used to resolve dotted paths.
 * @param {(s: string) => string} escapeHtml - HTML-escape utility.
 */
export function resolveRichText(html, data, escapeHtml) {
  if (!html) return "";
  const resolve = (path) => {
    const val = path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), data);
    return escapeHtml(val == null ? "" : String(val));
  };
  return html
    .replace(SPAN_RE, (_, path) => resolve(path))
    .replace(TOKEN_RE, (_, path) => resolve(path));
}

/**
 * Convert rich-text HTML into pill-styled HTML for the canvas designer.
 * Both placeholder formats become highlighted EJS markers (`&lt;%= path %&gt;`).
 */
export function pillifyRichText(html) {
  if (!html) return "";
  const PILL = "background:#DBEAFE;color:#1D4ED8;font-size:10px;padding:0 4px;border-radius:3px;font-family:monospace;";

  // Step 1: legacy span markup — preserve existing attributes while merging in the pill style.
  let out = html.replace(
    /<span([^>]*?)data-ejs-path="([^"]+)"([^>]*?)>([\s\S]*?)<\/span>/g,
    (_, pre, path, post) => {
      const attrs = `${pre}${post}`.trim();
      const existingStyleMatch = attrs.match(/style="([^"]*)"/);
      const existingStyle = existingStyleMatch ? existingStyleMatch[1] : "";
      const attrsNoStyle = attrs.replace(/style="[^"]*"/, "").trim();
      return `<span ${attrsNoStyle} data-ejs-path="${path}" style="${existingStyle};${PILL}">&lt;%= ${path} %&gt;</span>`;
    }
  );

  // Step 2: clean {{path}} tokens.
  out = out.replace(TOKEN_RE, (_, path) =>
    `<span data-ejs-path="${path}" style="${PILL}">&lt;%= ${path} %&gt;</span>`
  );
  return out;
}
